import { useState } from 'react';

export default function DebtCalculator() {
  const [debtAmount, setDebtAmount] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const calculateSavings = () => {
    const debt = parseFloat(debtAmount.replace(/,/g, ''));
    if (isNaN(debt) || debt <= 0) return null;

    const settlementRate = 0.5; // 50% average settlement
    const settlementAmount = debt * settlementRate;
    const savings = debt - settlementAmount;
    const monthlyPayment = settlementAmount / 36; // 36 month program

    return {
      originalDebt: debt,
      settlementAmount,
      savings,
      monthlyPayment,
      savingsPercent: 50
    };
  };

  const results = calculateSavings();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleCalculate = () => {
    if (results) {
      setShowResults(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !debtAmount) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('debtAmount', debtAmount);
      if (results) {
        formBody.append('estimatedSavings', formatCurrency(results.savings));
        formBody.append('estimatedMonthlyPayment', formatCurrency(results.monthlyPayment));
      }

      const response = await fetch('https://readdy.ai/api/form/d5r87t6tc768qf4bqh4g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-gradient-to-br from-teal-50 to-blue-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Free Debt Savings Calculator
          </h2>
          <p className="text-lg text-gray-600">
            See how much you could save with our debt settlement program
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          <div className="mb-8">
            <label className="block text-lg font-semibold text-gray-900 mb-3">
              How much debt do you have?
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-500">$</span>
              <input
                type="text"
                value={debtAmount}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setDebtAmount(value ? parseInt(value).toLocaleString() : '');
                  setShowResults(false);
                }}
                placeholder="25,000"
                className="w-full pl-12 pr-4 py-4 text-2xl border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>

          <button
            onClick={handleCalculate}
            disabled={!debtAmount}
            className="w-full bg-teal-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed mb-8"
          >
            Calculate My Savings
          </button>

          {showResults && results && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-gradient-to-r from-teal-500 to-blue-500 rounded-xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6 text-center">Your Estimated Savings</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="text-center">
                    <p className="text-sm opacity-90 mb-2">You Could Save</p>
                    <p className="text-4xl font-bold">{formatCurrency(results.savings)}</p>
                    <p className="text-sm opacity-90 mt-1">({results.savingsPercent}% reduction)</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm opacity-90 mb-2">Estimated Monthly Payment</p>
                    <p className="text-4xl font-bold">{formatCurrency(results.monthlyPayment)}</p>
                    <p className="text-sm opacity-90 mt-1">(36-month program)</p>
                  </div>
                </div>
              </div>

              <div className="border-t-2 border-gray-200 pt-6">
                {submitStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-check-line text-3xl text-green-600"></i>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h4>
                    <p className="text-gray-600">
                      A debt specialist will contact you within 24 hours to discuss your personalized debt relief plan.
                    </p>
                  </div>
                ) : (
                  <>
                    <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">
                      Get Your Free Personalized Debt Relief Plan
                    </h4>
                    <form onSubmit={handleSubmit} data-readdy-form id="debt-calculator">
                      <div className="space-y-4">
                        <input
                          type="text"
                          name="name"
                          placeholder="Your Name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        />
                        <input
                          type="email"
                          name="email"
                          placeholder="Email Address"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        />
                        <input
                          type="tel"
                          name="phone"
                          placeholder="Phone Number"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        />
                      </div>

                      {submitStatus === 'error' && (
                        <p className="text-red-600 text-sm mt-4 text-center">
                          Something went wrong. Please try again.
                        </p>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gray-900 text-white py-4 rounded-lg font-semibold hover:bg-gray-800 transition-all duration-300 mt-6 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:opacity-50"
                      >
                        {isSubmitting ? 'Submitting...' : 'Get My Free Consultation'}
                      </button>

                      <p className="text-xs text-gray-500 text-center mt-4">
                        No obligation. Your information is 100% confidential.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-gray-600 mt-6">
          * Results are estimates based on average settlement rates. Actual savings may vary based on your specific situation.
        </p>
      </div>
    </section>
  );
}
